import { Invitation, InvitationMessageType, websocket } from '@playcastdotio/Messaging';
import { getStateStore } from '../../stores';
import { sendMessage } from '../realtime';
import { hostState, invitationStateV2, machineIdState } from '../../stores/shared';

// Debounce settings
const INVITATION_DEBOUNCE_TIME = 50; // milliseconds
let invitationTimer: NodeJS.Timeout | undefined;
let lastInvitation: Partial<Invitation> | null = null;
let lastSaveToApi: boolean | undefined;

export const initializeInvitation = async () => {
  const state = getStateStore('ipc');
  if (hostState.currentState.edition !== 'standard') {
    console.log('Pavilion mode, skipping invitation initialization');
    return;
  }

  // Check for saved invitation in localStorage
  const savedInvitationJson = localStorage.getItem('currentInvitation');
  if (savedInvitationJson) {
    try {
      const savedInvitation = JSON.parse(savedInvitationJson) as Invitation;
      // Dont use updateState as we dont want to trigger a new invitation store message
      state.invitationStateV2.currentState = savedInvitation;
      await invalidateInvitation('Host replaced invitation with new one');
    } catch (error) {
      console.error('Failed to parse saved invitation:', error);
      // Clear invalid localStorage data
      localStorage.removeItem('currentInvitation');
    }
  }
};

// Debounced version of setInvitation
export const setInvitation = (newInvitation: Partial<Invitation>, saveToApi?: boolean) => {
  // console.log('setInvitation', newInvitation);
  if (hostState.currentState.edition !== 'standard') {
    console.log('Pavilion mode, skipping invitation save');
    return;
  }
  // Store the latest arguments
  lastInvitation = newInvitation;
  lastSaveToApi = saveToApi;

  // Clear any existing timer
  if (invitationTimer) {
    clearTimeout(invitationTimer);
  }

  // Set a new timer to process the invitation after the debounce time
  invitationTimer = setTimeout(() => {
    invitationTimer = undefined; // Reset the timer
    if (lastInvitation) {
      processInvitation(lastInvitation, lastSaveToApi);
      lastInvitation = null;
    }
  }, INVITATION_DEBOUNCE_TIME);
};

// Actual implementation of invitation processing
const processInvitation = (newInvitation: Partial<Invitation>, saveToApi?: boolean) => {
  const updatedInvitation: Invitation = {
    ...invitationStateV2.currentState,
    ...(newInvitation as Invitation),
  };
  if (updatedInvitation.type === 'lobby' || updatedInvitation.type === 'cafe') {
    // Always ensure machineId is set in the manifest
    if (!updatedInvitation.manifest.machineId) {
      updatedInvitation.manifest.machineId = machineIdState.currentState;
    }
  }
  // console.log('setInvitation2', updatedInvitation);
  invitationStateV2.updateState({ ...updatedInvitation });

  const jsonString = JSON.stringify(updatedInvitation);
  localStorage.setItem('currentInvitation', jsonString);

  if (saveToApi === false) {
    return;
  }
  // try {
  //   // intentionally throw to get stack trace
  //   throw new Error('test');
  // } catch (error: any) {
  //   console.error('INVALID, trace:', error.stack);
  // }
  sendMessage(websocket.generators.invitation(InvitationMessageType.INVITATION_STORE, { invitation: updatedInvitation }));
};

export const invalidateInvitation = async (reason?: string) => {
  const currentInvitation = invitationStateV2.currentState;
  if (currentInvitation.type !== 'lobby') {
    console.log('Cafe invitation, skipping invitation invalidate');
    return;
  }
  console.warn('INVALIDATING INVITATION', currentInvitation);

  // Only update if we have a valid invitation
  if (currentInvitation.id) {
    const currentInvalidReasons = currentInvitation.invalidReasons ?? [];
    const updatedInvitation: Invitation = {
      ...currentInvitation,
      invalid: true,
      invalidReasons: [...currentInvalidReasons, { reason: reason ?? 'Unknown host reason', timestamp: Date.now() }],
    };

    // Ensure machineId is set in the manifest
    if (updatedInvitation.type === 'lobby') {
      updatedInvitation.manifest.machineId = machineIdState.currentState;
    }

    // Update local state
    invitationStateV2.updateState(updatedInvitation);

    // Save to localStorage
    const jsonString = JSON.stringify(updatedInvitation);
    localStorage.setItem('currentInvitation', jsonString);

    // Send to API
    sendMessage(websocket.generators.invitation(InvitationMessageType.INVITATION_STORE, { invitation: updatedInvitation }));
  }

  await new Promise((resolve) => setTimeout(resolve, 120));

  // Then, send a valid invitation with no ID to generate another. Copy settings.
  const newInvitation: Invitation = {
    ...currentInvitation,
    id: '',
    createdAt: undefined,
    invalid: false,
    invalidReasons: [],
  };
  setInvitation(newInvitation, false);
};
